export type Granularity = 'weekly' | 'monthly' | 'semiannual' | 'yearly';

export const GRANULARITY_OPTIONS: { value: Granularity; label: string }[] = [
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensal' },
  { value: 'semiannual', label: 'Semestral' },
  { value: 'yearly', label: 'Anual' },
];

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export const MONTH_NAMES_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

const COMPARISON_LABELS: Record<Granularity, string> = {
  weekly: 'vs. semana anterior',
  monthly: 'vs. mês anterior',
  semiannual: 'vs. semestre anterior',
  yearly: 'vs. ano anterior',
};

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

interface CivilParts {
  year: number;
  month: number;
  day: number;
}

function getSaoPauloCivilParts(instant: Date): CivilParts {
  const formatted = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
  const [year, month, day] = formatted.split('-').map(Number);
  return { year, month, day };
}

function getIsoWeek(parts: CivilParts): { year: number; week: number } {
  const target = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  target.setUTCDate(target.getUTCDate() - ((target.getUTCDay() + 6) % 7) + 3);
  const year = target.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(year, 0, 4));
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7) + 3);
  const week = 1 + Math.round((target.getTime() - firstThursday.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return { year, week };
}

export function getCurrentPeriod(granularity: Granularity, reference: Date = new Date()): string {
  const parts = getSaoPauloCivilParts(reference);

  switch (granularity) {
    case 'weekly': {
      const isoWeek = getIsoWeek(parts);
      return `${isoWeek.year}-W${String(isoWeek.week).padStart(2, '0')}`;
    }
    case 'monthly':
      return `${parts.year}-${String(parts.month).padStart(2, '0')}`;
    case 'semiannual':
      return `${parts.year}-S${parts.month <= 6 ? 1 : 2}`;
    case 'yearly':
      return String(parts.year);
  }
}

export function getComparisonLabel(granularity: Granularity): string {
  return COMPARISON_LABELS[granularity];
}

function getIsoWeekMonday(year: number, week: number): Date {
  const january4 = new Date(Date.UTC(year, 0, 4));
  const mondayOfWeekOne = new Date(january4.getTime() - ((january4.getUTCDay() + 6) % 7) * DAY_MS);
  return new Date(mondayOfWeekOne.getTime() + (week - 1) * WEEK_MS);
}

function toWeeklyKey(mondayUtc: Date): string {
  const isoWeek = getIsoWeek({
    year: mondayUtc.getUTCFullYear(),
    month: mondayUtc.getUTCMonth() + 1,
    day: mondayUtc.getUTCDate(),
  });
  return `${isoWeek.year}-W${String(isoWeek.week).padStart(2, '0')}`;
}

function shiftPeriod(granularity: Granularity, period: string, offset: number): string {
  switch (granularity) {
    case 'weekly': {
      const [year, week] = period.split('-W').map(Number);
      const monday = getIsoWeekMonday(year, week);
      return toWeeklyKey(new Date(monday.getTime() + offset * WEEK_MS));
    }
    case 'monthly': {
      const [year, month] = period.split('-').map(Number);
      const monthIndex = year * 12 + (month - 1) + offset;
      return `${Math.floor(monthIndex / 12)}-${String((monthIndex % 12) + 1).padStart(2, '0')}`;
    }
    case 'semiannual': {
      const [year, half] = period.split('-S').map(Number);
      const halfIndex = year * 2 + (half - 1) + offset;
      return `${Math.floor(halfIndex / 2)}-S${(halfIndex % 2) + 1}`;
    }
    case 'yearly':
      return String(Number(period) + offset);
  }
}

export function getPreviousPeriod(granularity: Granularity, period: string): string {
  return shiftPeriod(granularity, period, -1);
}

export function getNextPeriod(granularity: Granularity, period: string): string {
  return shiftPeriod(granularity, period, 1);
}

export function isCurrentOrFuture(
  granularity: Granularity,
  period: string,
  reference: Date = new Date()
): boolean {
  return period >= getCurrentPeriod(granularity, reference);
}

export function isFuturePeriod(
  granularity: Granularity,
  period: string,
  reference: Date = new Date()
): boolean {
  return period > getCurrentPeriod(granularity, reference);
}

export const MIN_YEAR = 2000;

const MIN_PERIOD: Record<Granularity, string> = {
  weekly: `${MIN_YEAR}-W01`,
  monthly: `${MIN_YEAR}-01`,
  semiannual: `${MIN_YEAR}-S1`,
  yearly: String(MIN_YEAR),
};

export function getMinPeriod(granularity: Granularity): string {
  return MIN_PERIOD[granularity];
}

export function isPastPeriod(granularity: Granularity, period: string): boolean {
  return period < MIN_PERIOD[granularity];
}

export function isMinOrPast(granularity: Granularity, period: string): boolean {
  return period <= MIN_PERIOD[granularity];
}

export interface CalendarWeekDay {
  day: number;
  inMonth: boolean;
}

export interface CalendarWeek {
  key: string;
  days: CalendarWeekDay[];
}

export function getWeeksOfMonth(year: number, month: number): CalendarWeek[] {
  const firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
  const lastOfMonth = new Date(Date.UTC(year, month, 0));
  let monday = new Date(firstOfMonth.getTime() - ((firstOfMonth.getUTCDay() + 6) % 7) * DAY_MS);

  const weeks: CalendarWeek[] = [];
  while (monday.getTime() <= lastOfMonth.getTime()) {
    const days = Array.from({ length: 7 }, (_, index) => {
      const date = new Date(monday.getTime() + index * DAY_MS);
      return {
        day: date.getUTCDate(),
        inMonth: date.getUTCFullYear() === year && date.getUTCMonth() + 1 === month,
      };
    });
    weeks.push({ key: toWeeklyKey(monday), days });
    monday = new Date(monday.getTime() + WEEK_MS);
  }
  return weeks;
}

export function getWeekAnchorMonth(period: string): { year: number; month: number } {
  const [year, week] = period.split('-W').map(Number);
  const monday = getIsoWeekMonday(year, week);
  return { year: monday.getUTCFullYear(), month: monday.getUTCMonth() + 1 };
}

export function formatPeriodLabel(granularity: Granularity, period: string): string {
  switch (granularity) {
    case 'weekly': {
      const [year, week] = period.split('-W');
      return `Semana ${Number(week)} de ${year}`;
    }
    case 'monthly': {
      const [year, month] = period.split('-');
      return `${MONTH_NAMES[Number(month) - 1]} de ${year}`;
    }
    case 'semiannual': {
      const [year, half] = period.split('-S');
      return `${half}º semestre de ${year}`;
    }
    case 'yearly':
      return period;
  }
}
